import React from "react";
import "./OutletPage.css";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowRight, Play } from "lucide-react";

import heroImg     from "../image/gen-cafe-hero.png";
import quickImg    from "../image/gen-cafe-billing.png";
import variantsImg from "../image/gen-cafe-variants.png";
import inventoryImg from "../image/gen-cafe-inventory.png";
import analyticsImg from "../image/gen-cafe-analytics.png";
import kotImg      from "../image/gen-cafe-barista.png";
import loyaltyImg  from "../image/gen-cafe-loyalty.png";
import demoImg     from "../image/DemoForm_img.png";

const ACCENT   = "#8b5cf6";
const GRADIENT = "linear-gradient(135deg, #8b5cf6, #7c3aed)";
const GLOW     = "rgba(139,92,246,0.3)";

const css = `
  .op-cafe { --op-accent:#8b5cf6; --op-gradient:${GRADIENT}; --op-glow:${GLOW};
    --op-accent-10:rgba(139,92,246,0.1); --op-accent-30:rgba(139,92,246,0.3);
    --op-accent-05:rgba(139,92,246,0.05); }
`;

export default function CoffeeOutletPage() {
  return (
    <div className="op-page op-cafe">
      <style>{css}</style>

      {/* ── HERO ── */}
      <section className="op-hero">
        <div className="op-hero__bg">
          <img src={heroImg} alt="Cafe" />
          <div className="op-hero__gradient" />
          <div className="op-hero__noise" />
        </div>
        <div className="op-hero__content">
          <div className="op-hero__eyebrow">
            <span className="op-hero__eyebrow-dot" />
            Cafe &amp; Coffee Shop POS
          </div>
          <h1 className="op-hero__title">
            Brew faster,<br />
            <span className="op-accent-text">bill smarter</span>
          </h1>
          <p className="op-hero__subtitle">
            From the counter to the cup — KadaiPOS keeps your cafe running at full speed.
            Variations, add-ons, quick billing, loyalty — all in one screen.
          </p>
          <div className="op-hero__actions">
            <button
              className="op-btn-primary"
              onClick={() => document.getElementById("op-demo")?.scrollIntoView({ behavior: "smooth" })}
            >
              Get Free Demo <ArrowRight size={18} />
            </button>
            <button className="op-btn-ghost">
              <Play size={16} /> See It In Action
            </button>
          </div>
          <div className="op-hero__stats">
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">3x</span>
              <span className="op-hero__stat-label">Faster Billing</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">∞</span>
              <span className="op-hero__stat-label">Item Variants</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Live</span>
              <span className="op-hero__stat-label">Inventory Sync</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Auto</span>
              <span className="op-hero__stat-label">Loyalty Points</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENTO FEATURES ── */}
      <section className="op-bento">
        <div className="op-bento__header">
          <div className="op-section-eyebrow">What's Inside</div>
          <h2 className="op-section-title">Built for the rush hour</h2>
          <p className="op-section-subtitle">
            Cafes move fast. KadaiPOS moves faster — with a billing screen designed for speed and accuracy.
          </p>
        </div>

        <div className="op-bento__grid">
          {/* Accent stat card */}
          <div className="op-card op-card--narrow op-card--accent">
            <div className="op-card__body" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div style={{ fontSize: "3rem", marginBottom: "16px" }}>⚡</div>
              <h3 className="op-card__title" style={{ color: "#fff" }}>Quick Counter Billing</h3>
              <p className="op-card__desc" style={{ color: "rgba(255,255,255,0.75)" }}>
                One-tap item search, one-tap payment. Bills close in under 10 seconds on a busy Saturday morning.
              </p>
              <ul className="op-card__list">
                <li>Barcode / shortcut key billing</li>
                <li>Favourite items pinned to home screen</li>
                <li>Cash, card &amp; UPI payment in one flow</li>
              </ul>
            </div>
          </div>

          {/* Wide – Variants */}
          <div className="op-card op-card--wide">
            <img src={variantsImg} alt="Item Variations" className="op-card__img" />
            <div className="op-card__body">
              <div className="op-card__tag">🔀 Variations &amp; Add-ons</div>
              <h3 className="op-card__title">Cappuccino? Hot or iced. Oat milk or full fat?</h3>
              <p className="op-card__desc">
                Configure unlimited item variations and add-ons per product. The billing screen adapts per item — no confusing menus for staff.
              </p>
              <ul className="op-card__list">
                <li>Size variants (S / M / L) with separate pricing</li>
                <li>Add-on modifiers (extra shot, flavour syrup, toppings)</li>
                <li>Combo builder — set &amp; save popular combinations</li>
                <li>Variant-wise inventory deduction</li>
              </ul>
            </div>
          </div>

          {/* Half – Quick Billing image */}
          <div className="op-card op-card--half">
            <img src={quickImg} alt="Quick Billing" className="op-card__img op-card__img--tall" />
            <div className="op-card__body">
              <div className="op-card__tag">🧾 Quick Billing</div>
              <h3 className="op-card__title">One screen for everything</h3>
              <p className="op-card__desc">Your entire menu, variants, taxes, discounts and payment — all on a single billing screen that loads instantly.</p>
            </div>
          </div>

          {/* Half – Analytics */}
          <div className="op-card op-card--half">
            <img src={analyticsImg} alt="Customer Analytics" className="op-card__img op-card__img--tall" />
            <div className="op-card__body">
              <div className="op-card__tag">📊 Customer Analytics</div>
              <h3 className="op-card__title">Know your best customers by name</h3>
              <p className="op-card__desc">Track visit frequency, average spend, and favourite orders per customer. Run targeted SMS &amp; WhatsApp promotions.</p>
            </div>
          </div>

          {/* Full – Inventory */}
          <div className="op-card op-card--full" style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div className="op-card__body" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div className="op-card__tag">📦 Inventory Reports</div>
              <h3 className="op-card__title">Know exactly what's running low — before service starts</h3>
              <p className="op-card__desc">
                Live ingredient tracking deducts milk, coffee beans, syrups with every cup sold. Daily opening &amp; closing stock reports keep wastage in check.
              </p>
              <ul className="op-card__list">
                <li>Auto stock deduction from recipe per item sold</li>
                <li>Low-stock alert on billing screen during service</li>
                <li>Supplier purchase order raised from POS</li>
                <li>Wastage entry with reason code</li>
              </ul>
            </div>
            <img src={inventoryImg} alt="Inventory" className="op-card__img" style={{ height: "100%", minHeight: 260 }} />
          </div>
        </div>
      </section>

      {/* ── DEEP DIVE ── */}
      <section className="op-deepdive">
        <div className="op-deepdive__inner">
          <div className="op-bento__header">
            <div className="op-section-eyebrow">Loyalty &amp; CRM</div>
            <h2 className="op-section-title">Turn first-timers into regulars</h2>
          </div>

          <div className="op-deepdive__grid">
            <div className="op-deepdive__img-wrap">
              <img src={loyaltyImg} alt="Loyalty Wallet" />
              <div className="op-deepdive__img-badge">🎁 Loyalty Wallet</div>
            </div>
            <div className="op-deepdive__text">
              <h3>Reward points, wallet &amp; cashback — built in</h3>
              <p>
                Every bill automatically awards loyalty points. Customers redeem on their next visit from the billing screen — no separate app needed.
              </p>
              <ul className="op-check-list">
                {[
                  "Auto-points on every purchase (configurable ratio)",
                  "Wallet top-up &amp; redemption at checkout",
                  "Customer birthday &amp; anniversary campaigns",
                  "SMS / WhatsApp offer blasts to segmented lists",
                  "Referral tracking and reward disbursement"
                ].map((t, i) => (
                  <li key={i}>
                    <span className="op-check-icon"><CheckCircle size={13} /></span> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="op-deepdive__grid op-deepdive__grid--reverse" style={{ marginTop: 60 }}>
            <div className="op-deepdive__img-wrap">
              <img src={kotImg} alt="Station KOT" />
              <div className="op-deepdive__img-badge">🖨️ KOT System</div>
            </div>
            <div className="op-deepdive__text">
              <h3>Barista screen — no paper KOTs</h3>
              <p>
                Orders flow from counter billing to the barista display or printer instantly. Queue displayed by order number, status updated as each drink is ready.
              </p>
              <ul className="op-check-list">
                {[
                  "Order queued on barista screen by ticket number",
                  "Mark items ready — triggers customer token display",
                  "Separate routing: hot drinks, cold drinks, food",
                  "KOT history &amp; cancellation log",
                  "Kitchen Display System (KDS) compatible"
                ].map((t, i) => (
                  <li key={i}>
                    <span className="op-check-icon"><CheckCircle size={13} /></span> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── MINI STRIP ── */}
      <section className="op-strip">
        <div className="op-strip__grid">
          {[
            { icon: "📱", title: "Online Orders", desc: "Accept Swiggy, Zomato and website orders directly on your POS counter." },
            { icon: "🎫", title: "Coupon Engine", desc: "Create buy-1-get-1, flat discount, or minimum-bill coupons with expiry dates." },
            { icon: "🖨️", title: "Token System", desc: "Print customer tokens for order pickup — zero confusion on busy days." },
            { icon: "📈", title: "Sales Dashboard", desc: "Hourly, daily, item-wise sales — all on a live dashboard your manager can check anytime." },
          ].map((f, i) => (
            <div className="op-mini-card" key={i}>
              <div className="op-mini-card__icon">{f.icon}</div>
              <div className="op-mini-card__title">{f.title}</div>
              <p className="op-mini-card__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="op-cta" id="op-demo">
        <div className="op-cta__inner">
          <div className="op-cta__left">
            <h2>Get KadaiPOS running in your cafe this week</h2>
            <p>Quick setup — no technical skills needed. Our team installs and trains your staff on Day 1. You're billing faster by Day 2.</p>
            <div className="op-cta__form">
              <input className="op-cta__input" placeholder="Your name *" />
              <div className="op-cta__row">
                <input className="op-cta__input" placeholder="Phone number *" />
                <input className="op-cta__input" placeholder="City *" />
              </div>
              <input className="op-cta__input" placeholder="Cafe name *" />
              <button className="op-cta__submit">Book Free Demo →</button>
            </div>
          </div>
          <div className="op-cta__right-img">
            <img src={demoImg} alt="Schedule Demo" />
          </div>
        </div>
      </section>
    </div>
  );
}
